import React, { useCallback, useMemo, useState } from 'react';
import { Col, Row, Select, Spin, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useGetProgramMetricsQuery } from '../../redux/api/manufacturing';
import {
	PROGRAM_SCORE_WINDOW_LABELS,
	PROGRAM_TYPE_LABELS,
} from '../../interfaces/CamProgramming.interface';
import type {
	ProgramMetricsRow,
	ProgramScoreWindow,
	ProgramType,
} from '../../interfaces/CamProgramming.interface';

const { Text } = Typography;

const WINDOW_OPTIONS = (
	Object.keys(PROGRAM_SCORE_WINDOW_LABELS) as ProgramScoreWindow[]
).map((value) => ({ value, label: PROGRAM_SCORE_WINDOW_LABELS[value] }));

const DEFAULT_METRICS_WINDOW: ProgramScoreWindow = '90d';

const PROGRAM_TYPE_ORDER: ProgramType[] = ['cam', 'keyence', 'inspection_plan'];

const Metric: React.FC<{
	label: string;
	value: React.ReactNode;
	caption?: string;
	hint?: string;
}> = ({ label, value, caption, hint }) => (
	<div className="cam-metric">
		<div className="cam-metric__head">
			<span className="cam-metric__label">
				{label}
				{hint && (
					<Tooltip title={hint}>
						<QuestionCircleOutlined className="cam-metric__hint" />
					</Tooltip>
				)}
			</span>
		</div>
		<div className="cam-metric__value">{value}</div>
		{caption && <span className="cam-metric__caption">{caption}</span>}
	</div>
);

const formatMinutes = (value: number | null | undefined) =>
	value == null ? '—' : `${value} min`;

const formatCount = (value: number) => value.toLocaleString();

const METRICS_COLUMNS: ColumnsType<ProgramMetricsRow> = [
	{
		title: 'Programmer',
		dataIndex: 'full_name',
		fixed: 'left',
		width: 180,
		sorter: (a, b) => a.full_name.localeCompare(b.full_name),
		render: (_: unknown, row) => (
			<div>
				<div>{row.full_name}</div>
				{row.email && (
					<Text type="secondary" style={{ fontSize: 12 }}>
						{row.email}
					</Text>
				)}
			</div>
		),
	},
	{
		title: `${PROGRAM_TYPE_LABELS.cam} completed`,
		dataIndex: ['programs', 'cam'],
		align: 'right',
		width: 140,
		sorter: (a, b) => a.programs.cam - b.programs.cam,
		render: (_: unknown, row) => formatCount(row.programs.cam),
	},
	{
		title: `${PROGRAM_TYPE_LABELS.keyence} completed`,
		dataIndex: ['programs', 'keyence'],
		align: 'right',
		width: 140,
		sorter: (a, b) => a.programs.keyence - b.programs.keyence,
		render: (_: unknown, row) => formatCount(row.programs.keyence),
	},
	{
		title: `${PROGRAM_TYPE_LABELS.inspection_plan} completed`,
		dataIndex: ['programs', 'inspection_plan'],
		align: 'right',
		width: 140,
		sorter: (a, b) =>
			a.programs.inspection_plan - b.programs.inspection_plan,
		render: (_: unknown, row) => formatCount(row.programs.inspection_plan),
	},
	{
		title: 'First reviews',
		dataIndex: 'first_reviews',
		align: 'right',
		width: 110,
		sorter: (a, b) => a.first_reviews - b.first_reviews,
	},
	{
		title: 'Secondary reviews',
		dataIndex: 'secondary_reviews',
		align: 'right',
		width: 130,
		sorter: (a, b) => a.secondary_reviews - b.secondary_reviews,
	},
	{
		title: 'Issues reported',
		dataIndex: 'issues_reported',
		align: 'right',
		width: 120,
		sorter: (a, b) => a.issues_reported - b.issues_reported,
	},
	{
		title: 'Issues attributed',
		dataIndex: 'issues_attributed',
		align: 'right',
		width: 130,
		sorter: (a, b) => a.issues_attributed - b.issues_attributed,
	},
	{
		title: 'Feedback',
		dataIndex: 'feedback_entries',
		align: 'right',
		width: 100,
		sorter: (a, b) => a.feedback_entries - b.feedback_entries,
	},
];

const MetricsPanel: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

	const windowParam = searchParams.get('window');
	const metricsWindow: ProgramScoreWindow =
		windowParam && windowParam in PROGRAM_SCORE_WINDOW_LABELS
			? (windowParam as ProgramScoreWindow)
			: DEFAULT_METRICS_WINDOW;

	const setMetricsWindow = useCallback(
		(value: string) => {
			setSearchParams(
				(current) => {
					const next = new URLSearchParams(current);
					next.set('tab', 'metrics');
					next.set('window', value);
					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const { data, isLoading, isFetching } = useGetProgramMetricsQuery({
		window: metricsWindow,
	});

	const selectedRow = useMemo(
		() => data?.results.find((row) => row.user_id === selectedUserId) ?? null,
		[data, selectedUserId]
	);

	const userOptions = useMemo(
		() =>
			(data?.results ?? []).map((row) => ({
				value: row.user_id,
				label: row.full_name,
			})),
		[data]
	);

	if (isLoading || !data) {
		return (
			<div className="w-full bg-white border-b border-r border-l p-6">
				<div className="cam-section__placeholder">
					<Spin />
				</div>
			</div>
		);
	}

	const { shop, shop_overall } = data;
	const showingShop = selectedRow == null;
	const scopeLabel = showingShop ? 'shop' : selectedRow.full_name;

	const cardStats = {
		byType: Object.fromEntries(
			PROGRAM_TYPE_ORDER.map((programType) => [
				programType,
				showingShop
					? {
							programs: shop[programType].programs,
							avg_minutes: shop[programType].avg_minutes,
							timed_programs: shop[programType].timed_programs,
						}
					: {
							programs: selectedRow.programs[programType],
							avg_minutes: selectedRow.avg_programming_minutes[programType],
							timed_programs: null,
						},
			])
		) as Record<
			ProgramType,
			{
				programs: number;
				avg_minutes: number | null;
				timed_programs: number | null;
			}
		>,
		overall: showingShop
			? {
					programs: shop_overall.programs,
					avg_minutes: shop_overall.avg_minutes,
				}
			: {
					programs: selectedRow.total_programs,
					avg_minutes: selectedRow.overall_avg_programming_minutes,
				},
	};

	return (
		<div className="w-full bg-white border-b border-r border-l p-6">
			<div className="cam-grid-toolbar">
				<Select
					value={metricsWindow}
					onChange={setMetricsWindow}
					options={WINDOW_OPTIONS}
					style={{ width: 180 }}
				/>
				<Select
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="Shop average"
					value={selectedUserId}
					onChange={(value) => setSelectedUserId(value ?? null)}
					options={userOptions}
					style={{ width: 240 }}
				/>
				<Text type="secondary">
					{shop_overall.programs} completed{' '}
					{shop_overall.programs === 1 ? 'program' : 'programs'} ·{' '}
					{data.results.length}{' '}
					{data.results.length === 1 ? 'person' : 'people'} active
					{isFetching ? ' · refreshing…' : ''}
				</Text>
			</div>

			<Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
				{PROGRAM_TYPE_ORDER.map((programType) => {
					const stats = cardStats.byType[programType];
					return (
						<Col xs={12} md={6} key={programType}>
							<Metric
								label={`Avg ${PROGRAM_TYPE_LABELS[programType]} time`}
								value={formatMinutes(stats.avg_minutes)}
								caption={`${stats.programs} completed · ${scopeLabel}${
									showingShop &&
									stats.timed_programs != null &&
									stats.timed_programs < stats.programs
										? ` · ${stats.timed_programs} timed`
										: ''
								}`}
								hint={
									showingShop
										? 'Average programming duration logged on completed ' +
											`${PROGRAM_TYPE_LABELS[programType].toLowerCase()} ` +
											'in this window. Programs without a logged duration are ' +
											'counted in the total but excluded from the average.'
										: `${selectedRow.full_name}'s average programming duration ` +
											`for ${PROGRAM_TYPE_LABELS[programType].toLowerCase()} ` +
											'in this window. Only programs with a logged duration ' +
											'are included in the average.'
								}
							/>
						</Col>
					);
				})}
				<Col xs={12} md={6}>
					<Metric
						label="Avg overall programming time"
						value={formatMinutes(cardStats.overall.avg_minutes)}
						caption={`${cardStats.overall.programs} completed · ${scopeLabel}`}
						hint={
							showingShop
								? 'Average programming duration across CAM, Keyence and ' +
									'inspection plans combined.'
								: `${selectedRow.full_name}'s average programming duration ` +
									'across all program types in this window.'
						}
					/>
				</Col>
			</Row>

			<Table<ProgramMetricsRow>
				className="cam-entry-table cam-metrics-table"
				rowKey="user_id"
				size="small"
				columns={METRICS_COLUMNS}
				dataSource={data.results}
				scroll={{ x: 1100 }}
				rowClassName={(row) =>
					row.user_id === selectedUserId ? 'cam-table-row--selected' : ''
				}
				onRow={(row) => ({
					onClick: () => setSelectedUserId(row.user_id),
				})}
				pagination={{
					showSizeChanger: true,
					pageSizeOptions: ['10', '25', '50', '100'],
					defaultPageSize: 25,
					showTotal: (total) => `${total} programmers`,
				}}
			/>
		</div>
	);
};

export default MetricsPanel;
